import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useCreationModifierKeys } from "../useCreationModifierKeys";
import { useSelectedElementEditing } from "../useSelectedElementEditing";
import { useCreationModeOrchestration } from "../useCreationModeOrchestration";
import type {
  BboxRect,
  GroupedSmartSurveyorElement,
} from "components/SmartSurveyor/ReviewPage/types";
import type { ConfidenceLevel, LvMatch, PlanV3 } from "../../utils/types";
import { getFormDefaultValues } from "../../ElementInspectorV3/utils";
import { useTraversalModeV3 } from "../useTraversalModeV3";
import { useGroupSelectionState } from "../useGroupSelectionState";
import api from "utils/api";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { findElementGroupLabel } from "../../utils/findElementGroupLabel";
import { getFirstElementAcrossGroups } from "../../utils/getFirstElementAcrossGroups";
import { isPlaceholderGroupName } from "../../utils/isPlaceholderGroupName";
import { sumElementMultipliers } from "../../utils/sumElementMultipliers";
import { MIN_BBOX_SIDE_PDF } from "components/SmartSurveyor/shared/constants";
import { bboxCenter } from "utils/math/bboxCenter";
import { isSameBbox, translateBbox } from "./utils";
import { saveElementNote } from "../../utils/saveElementNote";
import { useAuth } from "contexts/AuthContext";
import { ALL_ELEMENTS_GROUP_LABEL, EMPTY_GROUP_META } from "./constants";
import { useResetOnPlanChange } from "./useResetOnPlanChange";
import { selectedElementGroupAfterHistory } from "../../HistoryDrawer/utils/selectedGroupAfterHistory";
import type { FollowHistoryChangesArgs } from "../../HistoryDrawer/utils/selectedGroupAfterHistory";
import type { RenameGroups } from "../../types";
import type { RenameSubmitAction } from "../../ApplyRenameToProjectModal";
import type {
  CommitRenameArgs,
  PendingRename,
  UseGroupSelectionV3Args,
} from "./types";

export const useGroupSelectionV3 = ({
  canEdit,
  selectedPlan,
  rooms,
  selectedRoom,
  onUpdateElement,
  onDeleteElement,
  onDeleteGroup,
  onBulkCreateElements,
  onRenameGroups,
  onMarkElementTypeReviewed,
  onSetElementTypeNameStatus,
  onSetElementGroupLvMatch,
  floorPlanPanelHandlers,
  isFloorPlanGroupApproved,
  toggleFloorPlanGroupApproval,
  interaction,
  traversing,
}: UseGroupSelectionV3Args) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [pendingRename, setPendingRename] = useState<PendingRename | null>(
    null,
  );
  const [renameSubmitAction, setRenameSubmitAction] =
    useState<RenameSubmitAction | null>(null);

  const {
    selectedGroupLabel,
    setSelectedGroupLabel,
    selectedElementId,
    setSelectedElementId,
    groupIds,
    byGroupId,
    allPlanElements,
    selectedGroupElements,
    selectedElement,
  } = useGroupSelectionState({ selectedPlan, rooms });

  const isAllElementsMode = selectedGroupLabel === ALL_ELEMENTS_GROUP_LABEL;

  const groupMeta = selectedPlan?.groupMeta ?? EMPTY_GROUP_META;

  const planElementTypeIds = useMemo(
    () => Object.values(groupMeta).map((meta) => meta.element_type_id),
    [groupMeta],
  );

  const totalElementCount = useMemo(
    () =>
      groupIds.reduce(
        (sum, groupId) =>
          sum + sumElementMultipliers({ elements: byGroupId[groupId] ?? [] }),
        0,
      ),
    [groupIds, byGroupId],
  );

  const currentElementTypeId =
    selectedGroupLabel && groupMeta[selectedGroupLabel]
      ? groupMeta[selectedGroupLabel].element_type_id
      : 0;

  const {
    interactionMode,
    isCreatingElement,
    isEditingElement,
    isMultiAddModeEnabled,
    creationContext,
    startCreatingElement,
    stopCreatingElement,
    setElementCreationStep,
    setElementCreationGroupName,
    toggleEditingElement,
    toggleMultiAdd,
    exitInteraction,
    resetToIdle,
  } = interaction;

  const { isTraversing, startTraversing } = traversing;

  const { seenIds, seenCount, markSeen, markSeenAndCheckCompletion } =
    useTraversalModeV3({
      canEdit,
      currentElementTypeId,
      selectedGroupElements,
      isFloorPlanGroupApproved,
      toggleFloorPlanGroupApproval,
    });

  const { selectedElementForm, draftSelectedElement } =
    useSelectedElementEditing({
      selectedElement,
      onSubmit: async ({ value, formApi }) => {
        if (!selectedElement || !selectedGroupLabel || selectedPlan === null) {
          return;
        }

        const oldGroupLabel = isAllElementsMode
          ? findElementGroupLabel({
              groupIds,
              byGroupId,
              elementId: selectedElement.id,
            })
          : selectedGroupLabel;

        if (!oldGroupLabel) return;

        const newGroupLabel = value.group_id;
        const groupChanged = newGroupLabel !== oldGroupLabel;
        const elementId = selectedElement.id;
        const noteContent = value.note.trim();
        const noteChanged =
          noteContent !== (selectedElement.note?.content ?? "");
        // A note-only save must not write a marker edit into the history.
        const markerChanged =
          groupChanged ||
          value.multiplier !== selectedElement.multiplier ||
          !isSameBbox({ first: value.bbox, second: selectedElement.bbox });
        try {
          const response = markerChanged
            ? await api.updateQuantitySurveyorElementMarker(elementId, {
                bboxX1: value.bbox.x1,
                bboxY1: value.bbox.y1,
                bboxX2: value.bbox.x2,
                bboxY2: value.bbox.y2,
                multiplier: value.multiplier,
                ...(groupChanged && { elementTypeName: newGroupLabel }),
              })
            : null;
          const note = noteChanged
            ? await saveElementNote({
                elementId,
                content: noteContent,
                existingNote: selectedElement.note,
                viewer: user,
              })
            : undefined;
          onUpdateElement({
            floorPlanId: selectedPlan.floorPlanId,
            fromGroupLabel: oldGroupLabel,
            elementId,
            changes: {
              ...(markerChanged && {
                bbox: value.bbox,
                multiplier: value.multiplier,
              }),
              ...(groupChanged &&
                response !== null && {
                  toGroupLabel: newGroupLabel,
                  newGroupElementTypeId: response.elementTypeId,
                }),
              ...(note !== undefined && { note }),
            },
          });
          trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ELEMENT_UPDATED, {
            groupChanged,
            markerChanged,
            noteChanged,
          });
          const resetFormToSubmittedValues = () =>
            formApi.reset({
              bbox: value.bbox,
              group_id: newGroupLabel,
              multiplier: value.multiplier,
              note: noteContent,
            });

          if (!groupChanged || isAllElementsMode) {
            resetFormToSubmittedValues();
          } else {
            const oldGroupElements = byGroupId[oldGroupLabel] ?? [];
            const currentIndex = oldGroupElements.findIndex(
              (element) => element.id === elementId,
            );
            const nextElement =
              oldGroupElements[currentIndex + 1] ??
              oldGroupElements[currentIndex - 1] ??
              null;
            const shouldStayInGroup = isTraversing && nextElement !== null;
            if (shouldStayInGroup) {
              setSelectedElementId(nextElement.id);
              formApi.reset(getFormDefaultValues(nextElement));
              floorPlanPanelHandlers?.current?.zoomToBbox(nextElement.bbox);
              if (currentElementTypeId) {
                markSeen(nextElement.id);
              }
            } else {
              setSelectedGroupLabel(newGroupLabel);
              resetFormToSubmittedValues();
            }
          }
          exitInteraction();
          toast.success(t("smartSurveyor.elementDetails.form.saved"));
        } catch {
          formApi.reset(getFormDefaultValues(selectedElement));
          toast.error(t("smartSurveyor.elementDetails.form.validationFailed"));
        }
      },
    });

  const selectElementWithoutZoom = useCallback(
    (element: GroupedSmartSurveyorElement | null) => {
      setSelectedElementId(element?.id ?? null);
      selectedElementForm.reset(getFormDefaultValues(element));
    },
    [setSelectedElementId, selectedElementForm],
  );

  const focusElement = useCallback(
    (element: GroupedSmartSurveyorElement | null) => {
      selectElementWithoutZoom(element);
      if (element) {
        floorPlanPanelHandlers?.current?.zoomToBbox(element.bbox);
      }
    },
    [selectElementWithoutZoom, floorPlanPanelHandlers],
  );

  const isInsideRoom = selectedRoom !== null;

  // A group select fits what the list shows: the room inside one, the whole plan otherwise
  const zoomToScope = useCallback(() => {
    if (selectedRoom) {
      floorPlanPanelHandlers?.current?.zoomToElements({
        elements: selectedRoom.points,
      });
      return;
    }
    floorPlanPanelHandlers?.current?.zoomToFit();
  }, [selectedRoom, floorPlanPanelHandlers]);

  const handleSelectAllElements = useCallback(() => {
    setSelectedGroupLabel(ALL_ELEMENTS_GROUP_LABEL);
    const firstElement = allPlanElements[0] ?? null;
    selectElementWithoutZoom(firstElement);
    zoomToScope();
    resetToIdle();
  }, [
    setSelectedGroupLabel,
    allPlanElements,
    selectElementWithoutZoom,
    zoomToScope,
    resetToIdle,
  ]);

  const handleSelectGroup = useCallback(
    (groupLabel: string) => {
      setSelectedGroupLabel(groupLabel);
      const firstElement = (byGroupId[groupLabel] ?? [])[0] ?? null;
      selectElementWithoutZoom(firstElement);
      zoomToScope();
      floorPlanPanelHandlers?.current?.flashRipple();
      resetToIdle();
    },
    [
      setSelectedGroupLabel,
      byGroupId,
      selectElementWithoutZoom,
      zoomToScope,
      floorPlanPanelHandlers,
      resetToIdle,
    ],
  );

  const selectNextGroupAfter = useCallback(
    (emptiedGroupLabel: string) => {
      const nextGroupLabel =
        groupIds.find((groupId) => groupId !== emptiedGroupLabel) ?? null;
      if (nextGroupLabel) {
        handleSelectGroup(nextGroupLabel);
        return;
      }
      resetToIdle();
      setSelectedGroupLabel(null);
      focusElement(null);
    },
    [
      groupIds,
      handleSelectGroup,
      resetToIdle,
      setSelectedGroupLabel,
      focusElement,
    ],
  );

  // An undo does not go through the handlers above, so a selected group it renamed or removed is followed here.
  const followHistoryChanges = useCallback(
    ({ floorPlanId, changes }: FollowHistoryChangesArgs) => {
      if (selectedPlan?.floorPlanId !== floorPlanId) return;
      const next = selectedElementGroupAfterHistory({
        selectedGroupLabel,
        changes,
        labelOfTypeId: (typeId) =>
          Object.keys(groupMeta).find(
            (label) => groupMeta[label].element_type_id === typeId,
          ),
      });
      if (next.kind === "select") {
        setSelectedGroupLabel(next.groupLabel);
      } else if (next.kind === "selectNext" && selectedGroupLabel !== null) {
        selectNextGroupAfter(selectedGroupLabel);
      }
    },
    [
      selectedPlan?.floorPlanId,
      selectedGroupLabel,
      groupMeta,
      setSelectedGroupLabel,
      selectNextGroupAfter,
    ],
  );

  const handleJumpToSelectedElementGroup = useCallback(() => {
    if (!selectedElement) return;
    // Open the selected element's own group, behaving exactly like picking that
    // group from the list (exits traversal via `resetToIdle`, so no half-loaded
    // "seen" progress is shown).
    handleSelectGroup(selectedElement.group_id);
  }, [selectedElement, handleSelectGroup]);

  const startTraversingAt = useCallback(
    (elementId: string) => {
      startTraversing();
      const element =
        selectedGroupElements.find((item) => item.id === elementId) ?? null;
      if (!element) return;
      focusElement(element);
      if (!isAllElementsMode) {
        markSeenAndCheckCompletion(elementId);
      }
    },
    [
      startTraversing,
      selectedGroupElements,
      focusElement,
      isAllElementsMode,
      markSeenAndCheckCompletion,
    ],
  );

  const resetPlanSelection = useCallback(
    (plan: PlanV3) => {
      resetToIdle();
      setSelectedGroupLabel(ALL_ELEMENTS_GROUP_LABEL);
      focusElement(getFirstElementAcrossGroups(plan));
    },
    [setSelectedGroupLabel, focusElement, resetToIdle],
  );

  const handleSelectElement = useCallback(
    (id: string) => {
      const element =
        selectedGroupElements.find((item) => item.id === id) ?? null;
      focusElement(element);
      if (isTraversing && !isAllElementsMode) {
        markSeenAndCheckCompletion(id);
      }
      exitInteraction();
    },
    [
      selectedGroupElements,
      focusElement,
      isTraversing,
      isAllElementsMode,
      markSeenAndCheckCompletion,
      exitInteraction,
    ],
  );

  const handleStartTraversing = useCallback(() => {
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_TRAVERSAL_STARTED, {
      groupKind: "elements",
    });
    startTraversing();
    const firstElement = selectedGroupElements[0];
    if (firstElement) {
      focusElement(firstElement);
      if (!isAllElementsMode) {
        markSeenAndCheckCompletion(firstElement.id);
      }
    }
  }, [
    startTraversing,
    selectedGroupElements,
    focusElement,
    isAllElementsMode,
    markSeenAndCheckCompletion,
  ]);

  const handleDeleteElement = useCallback(
    async (elementId: string) => {
      if (!selectedGroupLabel || !selectedPlan) return;

      const actualGroupLabel = isAllElementsMode
        ? findElementGroupLabel({ groupIds, byGroupId, elementId })
        : selectedGroupLabel;
      if (!actualGroupLabel) return;

      const groupElements = byGroupId[actualGroupLabel] ?? [];
      const isLastInGroup = groupElements.length === 1;

      exitInteraction();

      try {
        await api.deleteQuantitySurveyorElementMarker(elementId);
        const deletedElementTypeId =
          groupMeta[actualGroupLabel]?.element_type_id;
        if (deletedElementTypeId !== undefined) {
          trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ELEMENT_DELETED, {
            elementTypeId: String(deletedElementTypeId),
          });
        }

        onDeleteElement({
          floorPlanId: selectedPlan.floorPlanId,
          groupLabel: actualGroupLabel,
          elementId,
        });

        const elements = isAllElementsMode
          ? selectedGroupElements
          : groupElements;
        const currentIndex = elements.findIndex(
          (element) => element.id === elementId,
        );
        const nextElement =
          elements[currentIndex + 1] ?? elements[currentIndex - 1] ?? null;

        if (isAllElementsMode) {
          focusElement(nextElement);
        } else if (isLastInGroup) {
          resetToIdle();
          const nextGroupLabel =
            groupIds.find((id) => id !== actualGroupLabel) ?? null;
          setSelectedGroupLabel(nextGroupLabel);
          const firstElement = nextGroupLabel
            ? ((byGroupId[nextGroupLabel] ?? [])[0] ?? null)
            : null;
          focusElement(firstElement);
        } else {
          focusElement(nextElement);
          if (nextElement && isTraversing && currentElementTypeId) {
            markSeenAndCheckCompletion(
              nextElement.id,
              groupElements.length - 1,
            );
          }
        }
      } catch {
        toast.error(t("smartSurveyor.elementDetails.form.validationFailed"));
      }
    },
    [
      selectedGroupLabel,
      selectedPlan,
      isAllElementsMode,
      byGroupId,
      groupIds,
      groupMeta,
      selectedGroupElements,
      onDeleteElement,
      resetToIdle,
      exitInteraction,
      setSelectedGroupLabel,
      focusElement,
      isTraversing,
      currentElementTypeId,
      markSeenAndCheckCompletion,
      t,
    ],
  );

  const commitRename = useCallback(
    async ({
      oldGroupLabel,
      newGroupLabel,
      elementTypeId,
      applyToProject,
    }: CommitRenameArgs) => {
      try {
        const { affectedPlans } = await api.renameQuantitySurveyorElementType(
          elementTypeId,
          newGroupLabel,
          applyToProject,
        );
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_RENAMED, {
          groupKind: "elements",
          scope: applyToProject ? "project" : "plan",
        });

        onRenameGroups({ oldGroupLabel, newGroupLabel, affectedPlans });
        setSelectedGroupLabel(newGroupLabel);
      } catch {
        toast.error(t("smartSurveyor.groupInspector.renameFailed"));
      }
    },
    [onRenameGroups, setSelectedGroupLabel, t],
  );

  const renameGroupsFromOverview: RenameGroups = useCallback(
    ({ oldGroupLabel, newGroupLabel, affectedPlans }) => {
      onRenameGroups({ oldGroupLabel, newGroupLabel, affectedPlans });
      if (!selectedPlan || selectedGroupLabel !== oldGroupLabel) return;
      // A rename on other plans leaves this plan's group under its old name
      const isRenamedOnSelectedPlan = affectedPlans.some(
        (affectedPlan) => affectedPlan.floorPlanId === selectedPlan.floorPlanId,
      );
      if (!isRenamedOnSelectedPlan) return;
      setSelectedGroupLabel(newGroupLabel);
    },
    [onRenameGroups, selectedPlan, selectedGroupLabel, setSelectedGroupLabel],
  );

  const handleRenameGroup = useCallback(
    async (newGroupLabel: string) => {
      if (!selectedGroupLabel || !selectedPlan || isAllElementsMode) return;
      const meta = groupMeta[selectedGroupLabel];
      if (!meta) return;

      const renameArgs: CommitRenameArgs = {
        oldGroupLabel: selectedGroupLabel,
        newGroupLabel,
        elementTypeId: meta.element_type_id,
        applyToProject: false,
      };

      // A placeholder name collides with unrelated groups on other plans, so never fan out.
      if (isPlaceholderGroupName(selectedGroupLabel)) {
        await commitRename(renameArgs);
        return;
      }

      try {
        const preview = await api.getQuantitySurveyorElementTypeRenamePreview(
          meta.element_type_id,
          newGroupLabel,
        );

        if (preview.plans.length > 0) {
          setPendingRename({ ...renameArgs, plans: preview.plans });
          return;
        }
      } catch {
        // A failed preview shouldn't block the rename; fall through to the current plan only.
      }

      await commitRename(renameArgs);
    },
    [
      isAllElementsMode,
      selectedGroupLabel,
      selectedPlan,
      groupMeta,
      commitRename,
    ],
  );

  const confirmRenameForAllPlans = useCallback(async () => {
    if (!pendingRename) return;
    setRenameSubmitAction("allPlans");
    await commitRename({ ...pendingRename, applyToProject: true });
    setRenameSubmitAction(null);
    setPendingRename(null);
  }, [pendingRename, commitRename]);

  const confirmRenameForCurrentPlan = useCallback(async () => {
    if (!pendingRename) return;
    setRenameSubmitAction("currentPlan");
    await commitRename({ ...pendingRename, applyToProject: false });
    setRenameSubmitAction(null);
    setPendingRename(null);
  }, [pendingRename, commitRename]);

  const cancelPendingRename = useCallback(() => {
    setPendingRename(null);
  }, []);

  const handleDeleteGroup = useCallback(async () => {
    if (!selectedGroupLabel || !selectedPlan || isAllElementsMode) return;
    const meta = groupMeta[selectedGroupLabel];
    if (!meta) return;

    try {
      await api.deleteQuantitySurveyorElementType(meta.element_type_id);
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_DELETED, {
        groupKind: "elements",
        groupTypeId: String(meta.element_type_id),
        elementCount: (byGroupId[selectedGroupLabel] ?? []).length,
      });
      onDeleteGroup({
        floorPlanId: selectedPlan.floorPlanId,
        groupLabel: selectedGroupLabel,
      });

      selectNextGroupAfter(selectedGroupLabel);
    } catch {
      toast.error(t("smartSurveyor.groupInspector.deleteGroupFailed"));
    }
  }, [
    isAllElementsMode,
    selectedGroupLabel,
    selectedPlan,
    groupMeta,
    byGroupId,
    onDeleteGroup,
    selectNextGroupAfter,
    t,
  ]);

  const handleMarkReviewed = useCallback(async () => {
    if (!selectedGroupLabel || !selectedPlan || isAllElementsMode) return;
    const meta = groupMeta[selectedGroupLabel];
    if (!meta) return;
    await api.updateQuantitySurveyorElementType(meta.element_type_id, {
      nameStatus: "confident",
    });
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_MARKED_REVIEWED, {
      groupKind: "elements",
      groupTypeId: String(meta.element_type_id),
    });
    onMarkElementTypeReviewed({
      floorPlanId: selectedPlan.floorPlanId,
      elementTypeIds: [meta.element_type_id],
    });
  }, [
    isAllElementsMode,
    selectedGroupLabel,
    selectedPlan,
    groupMeta,
    onMarkElementTypeReviewed,
  ]);

  const handleSetNameStatus = useCallback(
    async (nameStatus: ConfidenceLevel) => {
      if (!selectedGroupLabel || !selectedPlan || isAllElementsMode) return;
      const meta = groupMeta[selectedGroupLabel];
      if (!meta) return;
      await api.updateQuantitySurveyorElementType(meta.element_type_id, {
        nameStatus,
      });
      onSetElementTypeNameStatus({
        floorPlanId: selectedPlan.floorPlanId,
        elementTypeIds: [meta.element_type_id],
        nameStatus,
      });
    },
    [
      isAllElementsMode,
      selectedGroupLabel,
      selectedPlan,
      groupMeta,
      onSetElementTypeNameStatus,
    ],
  );

  const handleSaveLvMatch = useCallback(
    async (lvMatch: LvMatch) => {
      if (!selectedPlan || !selectedElement) return;

      const targetGroupLabel = isAllElementsMode
        ? findElementGroupLabel({
            groupIds,
            byGroupId,
            elementId: selectedElement.id,
          })
        : selectedGroupLabel;

      if (!targetGroupLabel) return;

      const meta = groupMeta[targetGroupLabel];
      if (!meta) return;

      try {
        await api.updateQuantitySurveyorElementType(meta.element_type_id, {
          lvMatchId: lvMatch.lvId ?? null,
          lvMatchName: lvMatch.lvName ?? null,
          lvMatchReasoning: lvMatch.reasoning ?? null,
        });
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_LV_MATCH_SAVED, {
          groupKind: "elements",
          groupTypeId: String(meta.element_type_id),
        });
        onSetElementGroupLvMatch({
          floorPlanId: selectedPlan.floorPlanId,
          floorPlanElementGroupLabel: targetGroupLabel,
          lvMatch,
        });
      } catch {
        toast.error(t("smartSurveyor.lvMatchEditor.saveFailed"));
        throw new Error("Failed to save BOQ position");
      }
    },
    [
      isAllElementsMode,
      selectedGroupLabel,
      selectedElement,
      groupIds,
      byGroupId,
      selectedPlan,
      groupMeta,
      onSetElementGroupLvMatch,
      t,
    ],
  );

  const {
    creationModeState,
    creationFormElements,
    creationPreviewElements,
    creationActivePreviewElement,
    openCreationMode,
    handleCreateElements,
  } = useCreationModeOrchestration({
    selectedPlan,
    selectedElement,
    interactionMode,
    creationContext,
    groupIds,
    byGroupId,
    onBulkCreateElements,
    floorPlanPanelHandlers,
    startCreatingElement,
    stopCreatingElement,
    setElementCreationStep,
    setElementCreationGroupName,
    setSelectedGroupLabel,
    setSelectedElementId,
    selectedElementForm,
  });

  const selectPlanElement = useCallback(
    (id: string) => {
      if (isCreatingElement) {
        const index = creationFormElements.findIndex((row) => row.id === id);
        if (index !== -1) {
          creationModeState.setActiveIndex(index);
          const element = creationFormElements[index];
          floorPlanPanelHandlers?.current?.zoomToObject?.(
            bboxCenter({ bbox: element.bbox }),
          );
        }
        return;
      }
      if (!isTraversing) {
        startTraversingAt(id);
        return;
      }
      handleSelectElement(id);
    },
    [
      isCreatingElement,
      isTraversing,
      creationModeState,
      creationFormElements,
      handleSelectElement,
      floorPlanPanelHandlers,
      startTraversingAt,
    ],
  );

  const { creationModifier } = useCreationModifierKeys({
    isCreationMode: isCreatingElement,
    isEditingElement,
    isMultiAddModeEnabled,
    onEscape: exitInteraction,
  });

  const handlePickPositionFromMap = useCallback(
    (pos: { x: number; y: number }) => {
      if (isCreatingElement) {
        if (creationModeState.creationStep !== "adding-elements") return;
        if (creationModifier === "shift") {
          creationModeState.addElementAtPosition(pos);
        } else if (creationModifier === "ctrl") {
          const { form, activeIndex } = creationModeState;
          const activeRow = form.state.values.elements[activeIndex];
          if (!activeRow) return;
          form.setFieldValue(
            `elements[${activeIndex}].bbox`,
            translateBbox({ bbox: activeRow.bbox, newCenter: pos }),
          );
        }
      } else {
        selectedElementForm.setFieldValue(
          "bbox",
          translateBbox({
            bbox: selectedElementForm.state.values.bbox,
            newCenter: pos,
          }),
        );
      }
      floorPlanPanelHandlers?.current?.zoomToObject?.(pos);
    },
    [
      isCreatingElement,
      selectedElementForm,
      floorPlanPanelHandlers,
      creationModifier,
      creationModeState,
    ],
  );

  const handleChangeSelectedBbox = useCallback(
    (bbox: BboxRect) => {
      if (
        bbox.x2 - bbox.x1 < MIN_BBOX_SIDE_PDF ||
        bbox.y2 - bbox.y1 < MIN_BBOX_SIDE_PDF
      ) {
        return;
      }
      if (isCreatingElement) {
        if (creationModeState.creationStep !== "adding-elements") return;
        creationModeState.form.setFieldValue(
          `elements[${creationModeState.activeIndex}].bbox`,
          bbox,
        );
        return;
      }
      selectedElementForm.setFieldValue("bbox", bbox);
    },
    [isCreatingElement, selectedElementForm, creationModeState],
  );

  useResetOnPlanChange({
    selectedPlan,
    isInsideRoom,
    allPlanElements,
    resetToIdle,
    setSelectedGroupLabel,
    setSelectedElementId,
    selectedElementForm,
  });

  return {
    groupIds,
    byGroupId,
    planElementTypeIds,
    totalElementCount,
    isAllElementsMode,
    selectedGroupLabel,
    selectedGroupElements,
    selectedElement,
    selectedElementId,
    draftSelectedElement,
    handleSelectAllElements,
    handleSelectGroup,
    selectNextGroupAfter,
    followHistoryChanges,
    handleJumpToSelectedElementGroup,
    handleSelectElement,
    handleDeleteElement,
    handleDeleteGroup,
    handleRenameGroup,
    renameGroupsFromOverview,
    pendingRename,
    renameSubmitAction,
    confirmRenameForAllPlans,
    confirmRenameForCurrentPlan,
    cancelPendingRename,
    handleMarkReviewed,
    handleSetNameStatus,
    handleSaveLvMatch,
    resetPlanSelection,
    selectedElementForm,
    isEditingElement,
    resetToIdle,
    toggleEditMode: toggleEditingElement,
    exitInteraction,
    handlePickPositionFromMap,
    handleChangeSelectedBbox,
    handleCreateElements,
    stopCreating: stopCreatingElement,
    isCreationMode: isCreatingElement,
    openCreationMode,
    creationModeState,
    creationPreviewElements,
    creationActivePreviewElement,
    creationModifier,
    isMultiAddModeEnabled,
    toggleMultiAdd,
    selectPlanElement,
    handleStartTraversing,
    seenCount,
    seenIds,
  };
};

export type GroupSelectionV3Form = ReturnType<
  typeof useGroupSelectionV3
>["selectedElementForm"];
